import React, { useState, useEffect } from 'react';
import { Settings, Mail, FileText, Save, Send, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailSettings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('resend');
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  
  // Resend Config
  const [resendApiKey, setResendApiKey] = useState('');
  const [resendFromEmail, setResendFromEmail] = useState('');
  const [resendFromName, setResendFromName] = useState('');

  useEffect(() => {
    fetchResendConfig();
  }, []);

  const fetchResendConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/system/resend-config`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.is_configured) {
        setResendApiKey(response.data.resend_api_key_preview || '');
        setResendFromEmail(response.data.resend_email_from || '');
        setResendFromName(response.data.resend_from_name || '');
      }
    } catch (error) {
      console.error('Error fetching resend config:', error);
    }
  };

  const handleSaveResend = async () => {
    if (!resendApiKey || !resendFromEmail) {
      toast.error('API Key y Email remitente son requeridos');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API_URL}/api/system/resend-config`,
        {
          resend_api_key: resendApiKey,
          resend_email_from: resendFromEmail,
          resend_from_name: resendFromName
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('✅ Configuración de Resend guardada');
    } catch (error) {
      console.error('Error saving resend config:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_URL}/api/system/resend-config/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('✅ Email de prueba enviado exitosamente');
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar email de prueba');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Button
                onClick={() => navigate('/email-outbox')}
                variant="ghost"
                size="sm"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver a Bandeja
              </Button>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Settings className="h-8 w-8 text-indigo-600" />
              Configuración de Email
            </h1>
            <p className="text-gray-600 mt-2">
              Configura la conexión con Resend y gestiona plantillas
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('resend')}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'resend'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Conexión Resend
              </div>
            </button>
            <button
              onClick={() => setActiveTab('templates')}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'templates'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Plantillas de Email
              </div>
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'resend' && (
            <div className="max-w-2xl space-y-6">
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-blue-800 mb-2">
                  🔑 Configuración de Resend
                </h3>
                <p className="text-sm text-blue-700">
                  Necesitas una API Key de Resend para enviar emails. 
                  Obtén una gratis en <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="underline">resend.com</a>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  API Key de Resend: *
                </label>
                <input
                  type="password"
                  value={resendApiKey}
                  onChange={(e) => setResendApiKey(e.target.value)}
                  placeholder="re_xxxxxxxxxxxxxxxxxxxxx"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
                <p className="text-xs text-slate-500 mt-1">
                  La API Key debe comenzar con "re_"
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email Remitente: *
                </label>
                <input
                  type="email"
                  value={resendFromEmail}
                  onChange={(e) => setResendFromEmail(e.target.value)}
                  placeholder="noreply@tudominio.com"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Debe ser un email de un dominio verificado en Resend
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nombre Remitente:
                </label>
                <input
                  type="text"
                  value={resendFromName}
                  onChange={(e) => setResendFromName(e.target.value)}
                  placeholder="Tu Agencia de Seguros"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleSaveResend}
                  disabled={loading}
                  className="gap-2"
                >
                  <Save className="h-4 w-4" />
                  {loading ? 'Guardando...' : 'Guardar Configuración'}
                </Button>
                
                <Button
                  onClick={handleTestConnection}
                  disabled={testing || !resendApiKey}
                  variant="outline"
                  className="gap-2"
                >
                  <Send className="h-4 w-4" />
                  {testing ? 'Enviando...' : 'Probar Conexión'}
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'templates' && (
            <div>
              <p className="text-slate-600 mb-4">
                Las plantillas de email se gestionan desde una vista dedicada.
              </p>
              <Button
                onClick={() => navigate('/email-templates')}
                className="gap-2"
              >
                <FileText className="h-4 w-4" />
                Ir a Configuración de Plantillas
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
